import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Table, Button, Card, Tag, Space, message, Typography, Row, Col, Statistic,
  Modal, Form, Input, Select, InputNumber, Switch, Tabs, Collapse, Tooltip,
  Drawer, Descriptions, Spin, Popconfirm,
} from 'antd';
import {
  ArrowLeftOutlined, SaveOutlined, ThunderboltOutlined, SyncOutlined,
  EditOutlined, CheckCircleOutlined, CloseCircleOutlined, ExportOutlined,
  FilterOutlined, ReloadOutlined, EyeOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { KSGService } from '../api/services/ksg.service';
import { UsersService } from '../api/services/users.service';

const { Title, Text } = Typography;

const priorityColors = { critical: 'red', high: 'orange', medium: 'blue', low: 'default' };
const priorityLabels = { critical: 'Критический', high: 'Высокий', medium: 'Средний', low: 'Низкий' };

const taskTypeLabels = {
  land_legal: 'Земельно-правовые', permissive: 'Разрешительные', expert: 'Экспертные',
  design_engineering: 'Проектно-инженерные', financial_estimate: 'Финансово-сметные',
  control_supervision: 'Контрольно-надзорные', logistics: 'Логистические',
  coordination: 'Координационные', cadastral_registration: 'Кадастрово-регистрационные',
  organizational_regime: 'Организационно-режимные', household_administrative: 'Хозяйственно-административные',
};

export default function KSGDraftEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportForm] = Form.useForm();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editForm] = Form.useForm();
  const [filterStage, setFilterStage] = useState(null);
  const [filterBlock, setFilterBlock] = useState(null);
  const [activeTab, setActiveTab] = useState('table');

  const fetchDraft = async () => {
    setLoading(true);
    try {
      const res = await KSGService.getDraft(id);
      setDraft(res.data);
      setTasks(res.data.tasks || []);
    } catch { message.error('Ошибка загрузки черновика'); }
    finally { setLoading(false); }
  };

  const fetchUsers = () => UsersService.getUsers().then(r => setUsers(r.data)).catch(() => {});

  useEffect(() => { fetchDraft(); fetchUsers(); }, [id]);

  const stages = [...new Set(tasks.map(t => t.stage))];
  const blocks = [...new Set(tasks.map(t => t.block_code))];

  const filteredTasks = tasks.filter(t => {
    if (filterStage && t.stage !== filterStage) return false;
    if (filterBlock && t.block_code !== filterBlock) return false;
    return true;
  });

  const groupedTasks = {};
  filteredTasks.forEach(t => {
    const key = t.stage;
    if (!groupedTasks[key]) groupedTasks[key] = {};
    if (!groupedTasks[key][t.block_code]) groupedTasks[key][t.block_code] = { name: t.block_name, tasks: [] };
    groupedTasks[key][t.block_code].tasks.push(t);
  });

  const userName = (uid) => {
    if (!uid) return null;
    const u = users.find(u => String(u.id) === String(uid));
    return u ? (u.full_name || u.email) : null;
  };

  const handleToggleTask = async (taskId) => {
    try {
      const res = await KSGService.toggleDraftTask(taskId);
      setTasks(prev => prev.map(t => t.id === taskId ? res.data : t));
    } catch { message.error('Ошибка'); }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    editForm.setFieldsValue({
      name: task.name,
      description: task.description,
      task_type: task.task_type,
      volume: task.volume,
      duration_days: task.duration_days,
      priority: task.priority,
      assignee_id: task.assignee_id,
    });
    setEditModalOpen(true);
  };

  const handleSaveTask = async (values) => {
    if (!editingTask) return;
    try {
      const res = await KSGService.updateDraftTask(editingTask.id, values);
      setTasks(prev => prev.map(t => t.id === editingTask.id ? res.data : t));
      message.success('Задача обновлена');
      setEditModalOpen(false);
      setEditingTask(null);
    } catch { message.error('Ошибка сохранения'); }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const res = await KSGService.recalculateDraft(id);
      setTasks(res.data);
      message.success('Даты пересчитаны');
    } catch { message.error('Ошибка пересчёта'); }
    finally { setRecalculating(false); }
  };

  const handleExport = async (values) => {
    try {
      const res = await KSGService.exportDraftToProject(id, values);
      message.success(`Проект "${res.data.project_name}" создан с ${res.data.tasks_created} задачами`);
      setExportModalOpen(false);
      navigate(`/tasks`);
    } catch (err) {
      message.error(err?.response?.data?.detail || 'Ошибка экспорта');
    }
  };

  const activeTasks = tasks.filter(t => t.is_active);
  const totalCost = activeTasks.reduce((sum, t) => sum + (t.total_cost || 0), 0);
  const totalDuration = draft?.total_duration_days || 0;

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  if (!draft) return <div style={{ textAlign: 'center', padding: 80 }}>Черновик не найден</div>;

  const columns = [
    { title: 'Код', dataIndex: 'code', key: 'code', width: 140,
      render: (code, r) => (
        <Space>
          <Switch size="small" checked={r.is_active} onChange={() => handleToggleTask(r.id)} />
          <Text code style={{ fontSize: 11, opacity: r.is_active ? 1 : 0.5 }}>{code}</Text>
        </Space>
      ),
    },
    { title: 'Название задачи', dataIndex: 'name', key: 'name',
      render: (name, r) => (
        <Space>
          {r.is_milestone && <ThunderboltOutlined style={{ color: '#f59e0b' }} />}
          <Text strong={!r.is_milestone} style={{ opacity: r.is_active ? 1 : 0.5 }}>{name}</Text>
        </Space>
      ),
    },
    { title: 'Тип', dataIndex: 'task_type', key: 'task_type', width: 140,
      render: (t) => <Tag>{taskTypeLabels[t] || t}</Tag>,
    },
    { title: 'Дней', dataIndex: 'duration_days', key: 'duration_days', width: 70, align: 'center' },
    { title: 'Начало', dataIndex: 'start_date', key: 'start_date', width: 100,
      render: (d) => d ? dayjs(d).format('DD.MM.YY') : '—',
    },
    { title: 'Конец', dataIndex: 'due_date', key: 'due_date', width: 100,
      render: (d) => d ? dayjs(d).format('DD.MM.YY') : '—',
    },
    { title: 'Приоритет', dataIndex: 'priority', key: 'priority', width: 120,
      render: (p) => <Tag color={priorityColors[p]}>{priorityLabels[p] || p}</Tag>,
    },
    { title: 'Исполнитель', dataIndex: 'assignee_id', key: 'assignee_id', width: 160,
      render: (uid) => userName(uid) || <Text type="secondary">не назначен</Text>,
    },
    { title: 'Стоимость', dataIndex: 'total_cost', key: 'total_cost', width: 120, align: 'right',
      render: (v) => v ? `${v.toLocaleString()} ₽` : '—',
    },
    { title: '', key: 'actions', width: 60,
      render: (_, r) => <Button size="small" icon={<EditOutlined />} onClick={() => handleEditTask(r)} />,
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/ksg/drafts')} />
            <div>
              <h1 className="page-title" style={{ margin: 0 }}>{draft.name}</h1>
              <Tag color={draft.status === 'exported' ? 'green' : draft.status === 'ready' ? 'blue' : 'default'}>
                {draft.status === 'exported' ? 'Экспортирован' : draft.status === 'ready' ? 'Готов' : 'Черновик'}
              </Tag>
            </div>
          </Space>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={handleRecalculate} loading={recalculating}>
            Пересчитать даты
          </Button>
          <Button
            type="primary"
            icon={<ExportOutlined />}
            onClick={() => {
              exportForm.resetFields();
              exportForm.setFieldsValue({ project_name: draft.name });
              setExportModalOpen(true);
            }}
            disabled={draft.status === 'exported'}
            style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', border: 'none' }}
          >
            Экспорт в проект
          </Button>
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={4}>
          <Card size="small"><Statistic title="Всего задач" value={tasks.length} /></Card>
        </Col>
        <Col span={4}>
          <Card size="small"><Statistic title="Активных" value={activeTasks.length} valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
        <Col span={4}>
          <Card size="small"><Statistic title="Длительность" value={totalDuration} suffix="дней" /></Card>
        </Col>
        <Col span={6}>
          <Card size="small"><Statistic title="Общая стоимость" value={totalCost} formatter={(v) => `${v?.toLocaleString()} ₽`} /></Card>
        </Col>
        <Col span={6}>
          <Card size="small"><Statistic title="Дата начала" value={draft.project_start_date ? dayjs(draft.project_start_date).format('DD.MM.YYYY') : '—'} /></Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16, borderRadius: 16 }} styles={{ body: { padding: '12px 20px' } }}>
        <Space wrap>
          <FilterOutlined />
          <Select
            allowClear placeholder="Этап" value={filterStage} onChange={setFilterStage}
            style={{ width: 200 }}
            options={stages.map(s => ({ value: s, label: s }))}
          />
          <Select
            allowClear placeholder="Блок" value={filterBlock} onChange={setFilterBlock}
            style={{ width: 250 }}
            options={blocks.map(b => {
              const t = tasks.find(t => t.block_code === b);
              return { value: b, label: t ? t.block_name : b };
            })}
          />
        </Space>
      </Card>

      <Card style={{ borderRadius: 16 }} styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={filteredTasks}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200', '500'] }}
          scroll={{ x: 1200 }}
          rowClassName={(r) => !r.is_active ? 'row-disabled' : ''}
        />
      </Card>

      <Modal
        title="Редактировать задачу"
        open={editModalOpen}
        onCancel={() => { setEditModalOpen(false); setEditingTask(null); }}
        onOk={() => editForm.submit()}
        okText="Сохранить"
        width={600}
      >
        <Form form={editForm} layout="vertical" onFinish={handleSaveTask}>
          <Form.Item name="name" label="Название" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="task_type" label="Тип задачи">
                <Select options={Object.entries(taskTypeLabels).map(([v, l]) => ({ value: v, label: l }))} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="duration_days" label="Длительность (дни)">
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="priority" label="Приоритет">
                <Select options={Object.entries(priorityLabels).map(([v, l]) => ({ value: v, label: l }))} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="volume" label="Объём">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="assignee_id" label="Исполнитель">
                <Select
                  allowClear showSearch placeholder="Выберите исполнителя"
                  options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
                  filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Экспорт в проект"
        open={exportModalOpen}
        onCancel={() => setExportModalOpen(false)}
        onOk={() => exportForm.submit()}
        okText="Экспортировать"
        width={600}
      >
        <Form form={exportForm} layout="vertical" onFinish={handleExport}>
          <Form.Item name="project_name" label="Название проекта" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="curator_id" label="Куратор проекта" rules={[{ required: true, message: 'Выберите куратора' }]}>
            <Select
              showSearch placeholder="Выберите куратора"
              options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
              filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
          <Form.Item name="validator_id" label="Валидатор">
            <Select
              allowClear showSearch placeholder="Выберите валидатора"
              options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
              filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
          <Form.Item name="watcher_ids" label="Наблюдатели">
            <Select
              mode="multiple" showSearch placeholder="Выберите наблюдателей"
              options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
              filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
          <Card size="small" style={{ background: '#f0f5ff', marginBottom: 16 }}>
            <Text>Будет создано <Text strong>{activeTasks.length}</Text> задач. Исполнители назначаются вручную в редакторе черновика.</Text>
          </Card>
        </Form>
      </Modal>

      <style>{`
        .row-disabled { opacity: 0.4; }
      `}</style>
    </div>
  );
}
